import React, { useState } from 'react';
import { 
  Mail, 
  Send, 
  CheckCircle2, 
  XCircle, 
  Loader2,
  UserPlus,
  Users,
  CreditCard,
  LogOut,
  Vote,
  Bell,
  KeyRound,
  ShieldCheck,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Email templates configuration
const EMAIL_TEMPLATES = [
  {
    id: 'welcome',
    name: 'Boas-vindas',
    description: 'Enviado quando um novo usuário se cadastra',
    icon: UserPlus,
    color: 'bg-green-500/20 text-green-400',
  },
  {
    id: 'member_joined',
    name: 'Membro Entrou',
    description: 'Notifica o organizador quando alguém entra na viagem',
    icon: Users,
    color: 'bg-blue-500/20 text-blue-400',
  },
  {
    id: 'payment_received',
    name: 'Pagamento Recebido',
    description: 'Notifica o organizador sobre um pagamento',
    icon: CreditCard,
    color: 'bg-emerald-500/20 text-emerald-400',
  },
  {
    id: 'withdrawal_request',
    name: 'Solicitação de Desistência',
    description: 'Notifica o organizador sobre pedido de saída',
    icon: AlertTriangle,
    color: 'bg-amber-500/20 text-amber-400',
  },
  {
    id: 'withdrawal_approved',
    name: 'Desistência Aprovada',
    description: 'Notifica o membro que a desistência foi aprovada',
    icon: LogOut,
    color: 'bg-orange-500/20 text-orange-400',
  },
  {
    id: 'vote_created',
    name: 'Nova Votação',
    description: 'Notifica membros sobre uma nova votação',
    icon: Vote,
    color: 'bg-purple-500/20 text-purple-400',
  },
  {
    id: 'payment_reminder',
    name: 'Lembrete de Pagamento',
    description: 'Lembra o membro sobre pagamento pendente',
    icon: Bell,
    color: 'bg-red-500/20 text-red-400',
  },
  {
    id: 'verification_code',
    name: 'Código de Verificação',
    description: 'Envia código para verificar email',
    icon: ShieldCheck,
    color: 'bg-cyan-500/20 text-cyan-400',
  },
  {
    id: 'email_changed',
    name: 'Email Alterado',
    description: 'Notifica sobre alteração de email da conta',
    icon: Mail,
    color: 'bg-slate-500/20 text-slate-400',
  },
  {
    id: 'password_reset',
    name: 'Redefinir Senha',
    description: 'Envia código para redefinição de senha',
    icon: KeyRound,
    color: 'bg-indigo-500/20 text-indigo-400',
  },
  {
    id: 'password_changed',
    name: 'Senha Alterada',
    description: 'Confirma que a senha foi alterada',
    icon: ShieldCheck,
    color: 'bg-teal-500/20 text-teal-400',
  },
];

export default function AdminEmailTester() {
  const [testEmail, setTestEmail] = useState('');
  const [sendingStates, setSendingStates] = useState({});
  const [results, setResults] = useState({});

  const handleSendTest = async (templateId) => {
    if (!testEmail) {
      toast.error('Digite um email para enviar o teste');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(testEmail)) {
      toast.error('Email inválido');
      return;
    }

    setSendingStates(prev => ({ ...prev, [templateId]: true }));
    setResults(prev => ({ ...prev, [templateId]: null }));

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/email/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          template_id: templateId,
          to_email: testEmail,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setResults(prev => ({ ...prev, [templateId]: { success: true, message: data.message } }));
        toast.success(`Email "${EMAIL_TEMPLATES.find(t => t.id === templateId)?.name}" enviado!`);
      } else {
        setResults(prev => ({ ...prev, [templateId]: { success: false, message: data.detail || 'Erro ao enviar' } }));
        toast.error(data.detail || 'Erro ao enviar email');
      }
    } catch (error) {
      setResults(prev => ({ ...prev, [templateId]: { success: false, message: error.message } }));
      toast.error('Erro de conexão');
    } finally {
      setSendingStates(prev => ({ ...prev, [templateId]: false }));
    }
  };

  const handleSendAll = async () => {
    if (!testEmail) {
      toast.error('Digite um email para enviar os testes');
      return;
    }

    for (const template of EMAIL_TEMPLATES) {
      await handleSendTest(template.id);
      // Small delay between sends to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-email-tester">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Teste de Emails</h1>
          <p className="text-slate-400 mt-1">
            Envie emails de teste para validar todos os templates
          </p>
        </div>
      </div>

      {/* Email Input Card */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-orange-400" />
            Email de Destino
          </CardTitle>
          <CardDescription className="text-slate-400">
            Todos os emails de teste serão enviados para este endereço
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Input
              type="email"
              placeholder="seu-email@exemplo.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="flex-1 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
              data-testid="test-email-input"
            />
            <Button
              onClick={handleSendAll}
              disabled={!testEmail || Object.values(sendingStates).some(Boolean)}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="send-all-btn"
            >
              {Object.values(sendingStates).some(Boolean) ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Enviar Todos
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {EMAIL_TEMPLATES.map((template) => {
          const Icon = template.icon;
          const isSending = sendingStates[template.id];
          const result = results[template.id];

          return (
            <Card 
              key={template.id} 
              className="bg-slate-800 border-slate-700 hover:border-slate-600 transition-colors"
              data-testid={`email-template-${template.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${template.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-white text-sm">{template.name}</h3>
                    <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">
                      {template.description}
                    </p>
                    
                    {/* Result indicator */}
                    {result && (
                      <div className={`flex items-center gap-1 mt-2 text-xs ${
                        result.success ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {result.success ? (
                          <CheckCircle2 className="w-3 h-3" />
                        ) : (
                          <XCircle className="w-3 h-3" />
                        )}
                        <span className="truncate">{result.message}</span>
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleSendTest(template.id)}
                    disabled={!testEmail || isSending}
                    className="text-slate-400 hover:text-white hover:bg-slate-700"
                    data-testid={`send-${template.id}-btn`}
                  >
                    {isSending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Info Card */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20 text-blue-400">
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <h4 className="font-medium text-white">Sobre os Emails de Teste</h4>
              <p className="text-sm text-slate-400 mt-1">
                Os emails são enviados com dados fictícios para demonstrar o layout e conteúdo de cada template.
                Os emails são enviados via Resend a partir de <span className="text-orange-400">noreply@viajejunto.com.br</span>.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
